import { useState } from 'react';

const EXPENSE_TYPES = ['FOOD', 'GO OUT', 'BILLS', 'AMANDA', 'MARTIN', 'HOLIDAYS', 'HOUSE', 'OTHERS'];
const MONEY_SOURCES = ['MARTIN BANK', 'AMANDA BANK'];
const VALID_CURRENCIES = ['GBP', 'EUR', 'BRL'];

export const useParsedTransactions = () => {
  const [parsedTransactions, setParsedTransactions] = useState([]);
  const [rawInput, setRawInput] = useState('');
  const [parseErrors, setParseErrors] = useState([]);

  // Función para parsear una línea de transacción
  const parseTransactionLine = (line, lineIndex) => {
    const trimmedLine = line.trim();
    if (!trimmedLine) return null;

    // Regex para detectar formato nuevo: [DD/MM/YYYY] Descripción | Monto MONEDA | Banco
    const regexWithBank = /^\[(\d{2}\/\d{2}\/\d{4})\]\s*([^|]+)\s*\|\s*([0-9]+\.?[0-9]*)\s*(GBP|EUR|BRL)\s*\|\s*(.+)$/;
    // Regex para formato anterior: [DD/MM/YYYY] Descripción | Monto MONEDA
    const regexWithoutBank = /^\[(\d{2}\/\d{2}\/\d{4})\]\s*([^|]+)\s*\|\s*([0-9]+\.?[0-9]*)\s*(GBP|EUR|BRL)$/;
    
    let match = trimmedLine.match(regexWithBank);
    let bankName = null;
    
    if (match) {
      // Formato nuevo con banco
      bankName = match[5].trim();
    } else {
      // Intentar formato anterior sin banco
      match = trimmedLine.match(regexWithoutBank);
      if (!match) {
        return {
          error: `Línea ${lineIndex + 1}: Formato inválido. Usa: [DD/MM/YYYY] Descripción | Monto MONEDA | Banco`,
          line: trimmedLine
        };
      }
    }

    const [, dateStr, description, amountStr, currency] = match;
    
    // Validar fecha
    const dateParts = dateStr.split('/');
    const day = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]);
    const year = parseInt(dateParts[2]);
    
    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 2020 || year > 2030) {
      return {
        error: `Línea ${lineIndex + 1}: Fecha inválida ${dateStr}`,
        line: trimmedLine
      };
    }

    // Convertir fecha a formato ISO
    const isoDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    
    // Validar monto
    const amount = parseFloat(amountStr);
    if (isNaN(amount) || amount <= 0) {
      return {
        error: `Línea ${lineIndex + 1}: Monto inválido ${amountStr}`,
        line: trimmedLine
      };
    }

    // Validar moneda
    if (!VALID_CURRENCIES.includes(currency)) {
      return {
        error: `Línea ${lineIndex + 1}: Moneda inválida ${currency}. Usa: GBP, EUR, BRL`,
        line: trimmedLine
      };
    }

    return {
      id: `temp-${Date.now()}-${lineIndex}`,
      originalLine: trimmedLine,
      date: isoDate,
      merchantName: description.trim(),
      currency: currency,
      originalAmount: amount,
      bankName: bankName, // Nuevo campo banco
      expenseType: 'FOOD', // Default
      moneySource: 'MARTIN BANK', // Default
      hasSplit: false,
      splits: [
        { expenseType: 'FOOD', moneySource: 'MARTIN BANK', amount: '' },
        { expenseType: 'FOOD', moneySource: 'MARTIN BANK', amount: '' },
        { expenseType: 'FOOD', moneySource: 'MARTIN BANK', amount: '' }
      ]
    };
  };

  // Función para convertir monto a GBP
  const convertToGBP = (amount, currency, conversionRates) => {
    if (currency === 'GBP') return amount;
    if (currency === 'EUR') return amount / (conversionRates.EUR || 1.165);
    if (currency === 'BRL') return amount / (conversionRates.BRL || 6.36);
    return amount;
  };

  // Función principal para parsear el input
  const parseInput = (input, conversionRates) => {
    const lines = input.split('\n');
    const results = [];
    const errors = [];

    lines.forEach((line, index) => {
      const result = parseTransactionLine(line, index);
      if (result) {
        if (result.error) {
          errors.push(result);
        } else {
          // Calcular GBP amount
          result.gbpAmount = convertToGBP(result.originalAmount, result.currency, conversionRates);
          results.push(result);
        }
      }
    });

    setParsedTransactions(results);
    setParseErrors(errors);
    
    return { transactions: results, errors };
  };

  // Función para actualizar una transacción parseada
  const updateParsedTransaction = (id, field, value) => {
    setParsedTransactions(prev => 
      prev.map(transaction => {
        if (transaction.id === id) {
          const updated = { ...transaction, [field]: value };
          return updated;
        }
        return transaction;
      })
    );
  };

  // Función para actualizar split de una transacción
  const updateTransactionSplit = (id, splitIndex, field, value) => {
    setParsedTransactions(prev => 
      prev.map(transaction => {
        if (transaction.id === id) {
          const newSplits = [...transaction.splits];
          newSplits[splitIndex] = { ...newSplits[splitIndex], [field]: value };
          return { ...transaction, splits: newSplits };
        }
        return transaction;
      })
    );
  };

  // Función para limpiar todas las transacciones parseadas
  const clearParsedTransactions = () => {
    setParsedTransactions([]);
    setParseErrors([]);
    setRawInput('');
  };

  // Función para remover una transacción parseada específica
  const removeParsedTransaction = (id) => {
    setParsedTransactions(prev => prev.filter(t => t.id !== id));
  };

  return {
    parsedTransactions,
    parseErrors,
    rawInput,
    setRawInput,
    parseInput,
    updateParsedTransaction,
    updateTransactionSplit,
    clearParsedTransactions,
    removeParsedTransaction,
    convertToGBP,
    EXPENSE_TYPES,
    MONEY_SOURCES
  };
}; 